import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SessionService {
  readonly userId = signal<string>('');
  readonly displayName = signal<string>('');
  readonly email = signal<string>(''); 
  readonly persona = signal<string>('');
  readonly personas = signal<string[]>([]);

  constructor() {
    this.refreshFromStorage();
  }

  refreshFromStorage(): void {
    try {
      const storedUserId = localStorage.getItem('userID') || '';
      const storedName = localStorage.getItem('employeeName') || '';
      const storedEmail = localStorage.getItem('username') || '';
      const storedPersona = localStorage.getItem('persona') || '';
      const storedRolesRaw = localStorage.getItem('roles') || '[]';
      let storedRoles: string[] = [];
      try {
        const parsed = JSON.parse(storedRolesRaw);
        storedRoles = Array.isArray(parsed) ? parsed : [];
      } catch { storedRoles = []; }

      this.userId.set(storedUserId);
      this.displayName.set(storedName || '');
      this.email.set(storedEmail || '');
      this.persona.set(storedPersona);
      
      // Determine effective roles: use stored roles if available, otherwise fall back to persona
      let effectiveRoles: string[];
      if (storedRoles.length) {
        effectiveRoles = storedRoles;
      } else if (storedPersona) {
        effectiveRoles = [storedPersona];
      } else {
        effectiveRoles = [];
      }
      
      this.personas.set(effectiveRoles);
      if (!storedPersona && effectiveRoles.length) {
        this.persona.set(effectiveRoles[0]);
        try { localStorage.setItem('persona', effectiveRoles[0]); } catch {
          // Ignore localStorage errors (e.g., in private browsing mode)
        }
      }
    } catch {
      // No-op fallback
    }
  }

  setPersona(persona: string): void {
    this.persona.set(persona);
    try { localStorage.setItem('persona', persona); } catch {
      // Ignore localStorage errors (e.g., in private browsing mode)
    }
  }

  clear(): void {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenType');
      localStorage.removeItem('userID');
      localStorage.removeItem('employeeName');
      localStorage.removeItem('username');
      localStorage.removeItem('persona');
      localStorage.removeItem('roles');
    } catch {
      // Ignore localStorage errors (e.g., in private browsing mode)
    }
    this.userId.set('');
    this.displayName.set('');
    this.email.set('');
    this.persona.set('');
    this.personas.set([]);
  }

  /**
   * Persona checking utilities
   * Used by components to determine current user's persona
   */
  isTransloading(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('transload');
  }

  isGatehouse(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('gate');
  }

  isScheduler(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('schedule');
  }

  isReceiver(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('receiv');
  }

  isShunting(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('shunting') || p.includes('shunt');
  }

  isGrower(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('grower') || p.includes('grow');
  }

  isAdmin(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('admin');
  }

  isAudit(): boolean {
    const p = (this.persona() || '').toLowerCase();
    return p.includes('audit');
  }

  /**
   * Check if user is authenticated (has a userId)
   */
  isAuthenticated(): boolean {
    return !!this.userId();
  }

  /**
   * Check if user has a specific role (case insensitive)
   */
  hasRole(role: string): boolean {
    const normalizedRole = role.toLowerCase();
    return this.personas().some(p => p.toLowerCase() === normalizedRole);
  }
}


